<template>
  <div class="screen">
    <h2>Codenames</h2>
    <div v-if="roomError" class="notice notice--bad">{{ roomError }}</div>

    <template v-if="!roomId">
      <p class="muted">
        Two teams of spies. The spymasters see who everyone really is and give
        one-word clues; their operatives touch the codenames they mean —
        and try very hard not to touch the assassin.
      </p>
      <!-- Your async games, across every game on the site (atrAsync, kept
           by the hub's Lambda): the ones waiting on you first. -->
      <div v-if="myGames.length" class="my-games">
        <h3 class="my-games__title">Your games</h3>
        <a v-for="g in myGames" :key="g.key" class="my-game" :class="{ 'is-my-turn': g.myTurn }" :href="g.url">
          <span class="my-game__name">{{ g.name }}<span v-if="g.code" class="my-game__code"> · {{ g.code }}</span></span>
          <span class="my-game__who">{{ g.myTurn ? 'Your turn' : (g.status === 'lobby' ? 'Not started' : g.waitingOn && g.waitingOn.length ? `Waiting on ${g.waitingOn.join(', ')}` : 'In progress') }}</span>
        </a>
      </div>

      <button class="primary" @click="host">Start a new game</button>
      <!-- Async play (2026-10-04): the room waits for people instead of
           handing their seats to bots, and the hub tells them when it's
           their turn. See src/store/asyncPlay.js. -->
      <label class="async-toggle">
        <input v-model="playAsync" type="checkbox" />
        <span>Play over days — the game waits for each person, and tells them when it's their turn</span>
      </label>
      <TurnBell :game="gamePrefix" />
      <div class="field">
        <label for="code">Or join with a code</label>
        <div class="join-row">
          <input id="code" v-model="code" class="code-input" maxlength="4"
                 autocomplete="off" spellcheck="false" placeholder="SPYS" @keyup.enter="join" />
          <button class="secondary" :disabled="code.length < 4" @click="join">Join</button>
        </div>
      </div>
      <button class="secondary" @click="$emit('back')">Back</button>
    </template>

    <template v-else>
      <div class="room-code">
        <span class="room-code__label">Room code</span>
        <strong class="room-code__value">{{ roomMeta?.roomCode }}</strong>
        <button class="secondary" @click="share">{{ shareLabel }}</button>
      </div>

      <div class="teams">
        <div v-for="team in ['red', 'blue']" :key="team" class="team-col" :class="`team-col--${team}`">
          <h3 class="team-col__name">{{ team === 'red' ? 'Red' : 'Blue' }}</h3>

          <div class="team-col__slots">
            <div
              v-for="s in slotsFor(team, 'spymaster')"
              :key="s.uid"
              class="seat"
            >
              <span class="seat__name">{{ s.displayName }}</span>
              <span class="seat__tag">spymaster</span>
              <span v-if="s.uid === myUid" class="seat__tag">you</span>
              <span class="seat__dot" :class="{ 'seat__dot--on': presence[s.uid] }" />
            </div>
            <div
              v-for="s in slotsFor(team, 'operative')"
              :key="s.uid"
              class="seat"
            >
              <span class="seat__name">{{ s.displayName }}</span>
              <span v-if="s.uid === myUid" class="seat__tag">you</span>
              <span class="seat__dot" :class="{ 'seat__dot--on': presence[s.uid] }" />
            </div>
          </div>

          <div class="team-col__actions">
            <button
              class="secondary team-col__join"
              :disabled="isMine(team, 'spymaster')"
              @click="choose(team, 'spymaster')"
            >Be spymaster</button>
            <button
              class="secondary team-col__join"
              :disabled="isMine(team, 'operative')"
              @click="choose(team, 'operative')"
            >Join operatives</button>
          </div>
        </div>
      </div>

      <div v-if="isHost" class="field">
        <label>Words</label>
        <div class="word-sets">
          <button
            v-for="set in wordSets"
            :key="set.key"
            class="word-set"
            :class="{ 'is-selected': wordSetKey === set.key }"
            :title="set.blurb"
            @click="chooseSet(set.key)"
          >{{ set.name }}</button>
          <button
            v-if="roomMeta?.customWords"
            class="word-set word-set--custom"
            :class="{ 'is-selected': wordSetKey === 'custom' }"
            @click="chooseSet('custom')"
          >“{{ roomMeta.customLabel }}”</button>
        </div>
        <div v-if="customSetsAvailable" class="join-row">
          <input
            v-model="wordPrompt"
            class="word-prompt"
            placeholder="Or invent one: dinosaurs, the 80s, our kitchen…"
            maxlength="200"
            :disabled="generatingWords"
            @keyup.enter="invent"
          />
          <button
            class="secondary"
            :disabled="!wordPrompt.trim() || generatingWords"
            @click="invent"
          >{{ generatingWords ? 'Inventing…' : 'Invent' }}</button>
        </div>
      </div>
      <p v-else class="muted">
        Words: <strong>{{ wordSetName }}</strong>
      </p>

      <p class="muted">
        Each team needs a spymaster and at least one operative — four of you
        for the classic game. Share the link and get them in here.
      </p>

      <button v-if="isHost" class="primary" :disabled="Boolean(startProblem)" @click="deal">
        {{ startProblem || 'Start' }}
      </button>
      <p v-else class="muted">Waiting for the host to start…</p>
      <button class="secondary" @click="leave">Leave room</button>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { ref as dbRef, onValue } from 'firebase/database';
import { db } from '@/firebase';
import { ASYNC_INDEX } from '@/store/asyncPlay.js';
import { GAMES } from '@/store/multiplayer';
import TurnBell from '@/components/shared/TurnBell.vue';
import { WORD_SETS } from '@/engine/wordSets';

export default {
  name: 'RoomLobby',
  components: { TurnBell },
  emits: ['back'],
  data() {
    return {
      playAsync: false,
      myGamesRows: {},
      code: '', shareLabel: 'Copy link', wordPrompt: '', wordSets: WORD_SETS,
    };
  },
  computed: {
    ...mapState(['roomId', 'roomMeta', 'presence', 'roomError', 'generatingWords']),
    gamePrefix() { return GAMES.replace(/Games$/, ''); },
    myGames() {
      return Object.entries(this.myGamesRows || {})
        .map(([key, row]) => ({ key, ...row }))
        .sort((a, b) => (b.myTurn - a.myTurn) || (b.updatedAt || 0) - (a.updatedAt || 0));
    },
    ...mapGetters(['isHost', 'myUid', 'seats', 'startProblem', 'customSetsAvailable', 'wordSetKey', 'wordSetName']),
  },
  mounted() { this.$store.dispatch('sweepRooms'); this.followMyGames(); },
  beforeUnmount() { if (this.offMyGames) this.offMyGames(); },
  watch: {
    code(v) { this.code = String(v || '').toUpperCase().replace(/[^A-Z0-9]/g, ''); },
  },
  methods: {
    slotsFor(team, role) {
      return this.seats.filter((s) => s.team === team && s.role === role);
    },
    isMine(team, role) {
      const me = this.seats.find((s) => s.uid === this.myUid);
      return me?.team === team && me?.role === role;
    },
    choose(team, role) { this.$store.dispatch('chooseSlot', { team, role }); },
    chooseSet(key) { this.$store.dispatch('chooseWordSet', key); },
    invent() {
      if (!this.wordPrompt.trim() || this.generatingWords) return;
      this.$store.dispatch('generateCustomSet', this.wordPrompt.trim());
    },
    followMyGames() {
      const uid = this.$store.state.user?.uid;
      if (!uid || !db) return;
      this.offMyGames = onValue(dbRef(db, `${ASYNC_INDEX}/${uid}`), (snap) => { this.myGamesRows = snap.val() || {}; });
    },
    host() { this.$store.dispatch('hostRoom', { async: this.playAsync }); },
    join() { this.$store.dispatch('joinRoomByCode', this.code); },
    deal() { this.$store.dispatch('deal'); },
    leave() { this.$store.dispatch('leaveRoom'); },
    async share() {
      // The invite carries the CODE as well as the link (Matt, 2026-10-04): on
      // an iPhone a link opens in Safari, never in the installed app, so a
      // friend who has the app wants the code to type into it instead.
      const code = this.roomMeta?.roomCode;
      const text = `Join me for Codenames${this.roomMeta?.async ? ' — we play over days, so take your turns whenever' : ''}. Code ${code}`;
      const url = `${window.location.origin}/?join=${this.roomMeta?.roomCode}`;
      try {
        if (navigator.share) { await navigator.share({ title: 'Codenames', text, url }); return; }
        await navigator.clipboard.writeText(`${text} · ${url}`);
        this.shareLabel = 'Copied';
        window.setTimeout(() => { this.shareLabel = 'Copy link'; }, 1800);
      } catch { this.shareLabel = 'Copy link'; }
    },
  },
};
</script>

<style scoped>
.my-games { display: flex; flex-direction: column; gap: 0.4rem; margin-bottom: 0.5rem; }
.my-games__title { margin: 0 0 0.2rem; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.08em; opacity: 0.7; }
.my-game { display: flex; justify-content: space-between; gap: 0.75rem; padding: 0.6rem 0.8rem; border: 1px solid currentColor; border-radius: 0.6rem; text-decoration: none; color: inherit; opacity: 0.85; }
.my-game.is-my-turn { opacity: 1; font-weight: 700; }
.my-game__code { opacity: 0.7; font-weight: 400; }
.async-toggle { display: flex; gap: 0.6rem; align-items: flex-start; font-size: 0.9rem; line-height: 1.35; margin: 0.4rem 0; }
.async-toggle input { margin-top: 0.2rem; }
</style>
